import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Divider, Skeleton, Alert, Button, alpha, useTheme,
} from '@mui/material';
import {
  PointOfSale, CalendarMonth, WarningAmber, Refresh, ReceiptLong,
} from '@mui/icons-material';
import { format } from 'date-fns';

import feePaymentService from '../../services/feePaymentService';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useSubscription } from '../../context/SubscriptionContext';
import RecordFeePaymentDialog from '../fees/RecordFeePaymentDialog';
import InvoiceDetailDialog from '../fees/InvoiceDetailDialog';
import StudentFeeActivity from './StudentFeeActivity';

import { StudentFeeSummary } from '../../types/fees';
import ChallanNumberCell from '../fees/ChallanNumberCell';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface Props {
  studentId: string;
  /** Bump to force a refetch (used after mutations elsewhere). */
  refreshKey?: number;
  /** Bubble-up callback so the drawer can also refresh siblings (history, etc.). */
  onMutated?: () => void;
}

/**
 * StudentFeesTab — a compact, decision-first fee view for the student
 * profile drawer.
 *
 * Layout
 * ------
 *   1. KPI strip (Billed / Collected / Outstanding + Overdue chip).
 *   2. "Record payment" CTA when there's anything to collect.
 *   3. Outstanding invoices (compact table, click → detail dialog).
 *   4. Recent payments (compact list).
 *
 * All of it loads in a single API call.
 */
const StudentFeesTab: React.FC<Props> = ({ studentId, refreshKey, onMutated }) => {
  const theme = useTheme();
  const { canRead, canWrite } = useModulePermissions();
  const { hasFeature } = useSubscription();

  const canReadFees = canRead('fees');
  const canWriteFees = canWrite('fees');
  const hasFees = hasFeature('fees');

  const [summary, setSummary] = useState<StudentFeeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const currencyFmt = useCurrencyFormat();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await feePaymentService.getStudentFeeSummary(studentId);
      setSummary(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load fee summary');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { if (canReadFees && hasFees) load(); }, [load, refreshKey, tick, canReadFees, hasFees]);

  const collectedRatio = useMemo(() => {
    if (!summary || summary.kpis.billed <= 0) return 0;
    return Math.min(1, summary.kpis.collected / summary.kpis.billed);
  }, [summary]);

  if (!hasFees) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Fees module is not included in your plan.
        </Typography>
      </Paper>
    );
  }

  if (!canReadFees) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          You don't have permission to view fees for this student.
        </Typography>
      </Paper>
    );
  }

  if (loading && !summary) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={100} />
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={100} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" variant="outlined" action={
        <Button size="small" onClick={load} startIcon={<Refresh fontSize="small" />}>Retry</Button>
      }>
        {error}
      </Alert>
    );
  }

  if (!summary) return null;

  const { kpis, outstandingInvoices, assignment } = summary;
  const hasBilling = kpis.invoiceCount > 0;
  const hasPlan = Boolean(assignment);

  return (
    <Stack spacing={2}>
      {/* KPI strip */}
      <Paper
        variant="outlined"
        sx={{
          p: 2, borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total billed</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
              {currencyFmt(kpis.billed)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              across {kpis.invoiceCount} challan{kpis.invoiceCount === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Collected</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25, color: theme.palette.success.dark, fontVariantNumeric: 'tabular-nums' }}>
              {currencyFmt(kpis.collected)}
            </Typography>
            {kpis.billed > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Box sx={{
                  height: 4, borderRadius: 2, overflow: 'hidden',
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                }}>
                  <Box sx={{
                    width: `${collectedRatio * 100}%`,
                    height: '100%',
                    bgcolor: theme.palette.success.main,
                    transition: 'width 300ms',
                  }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {Math.round(collectedRatio * 100)}% collected
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                mt: 0.25,
                color: kpis.outstanding > 0 ? theme.palette.warning.dark : theme.palette.success.dark,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {currencyFmt(kpis.outstanding)}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
              {kpis.outstandingInvoiceCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {kpis.outstandingInvoiceCount} unpaid
                </Typography>
              )}
              {kpis.overdueInvoiceCount > 0 && (
                <Chip
                  size="small"
                  icon={<WarningAmber sx={{ fontSize: '0.85rem !important' }} />}
                  label={`${kpis.overdueInvoiceCount} overdue`}
                  variant="outlined"
                  sx={{
                    height: 20, fontSize: '0.68rem', fontWeight: 600,
                    borderColor: alpha(theme.palette.error.main, 0.32),
                    color: theme.palette.error.dark,
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
            </Stack>
          </Box>
        </Stack>

        {kpis.refunded > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Refunded to the family:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{currencyFmt(kpis.refunded)}</Box>
            {' '}— collected less refunds is {currencyFmt(kpis.collected - kpis.refunded)}.
          </Typography>
        )}

        {kpis.advanceHeld > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Advance held:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'success.dark', fontVariantNumeric: 'tabular-nums' }}>{currencyFmt(kpis.advanceHeld)}</Box>
            {' '}— used against the next challans when collecting.
          </Typography>
        )}

        {canWriteFees && kpis.outstanding > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Button
              fullWidth
              variant="contained"
              disableElevation
              startIcon={<PointOfSale />}
              onClick={() => setPayOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Record payment · {currencyFmt(kpis.outstanding)} outstanding
            </Button>
          </>
        )}
      </Paper>

      {/* Active-plan card — shown whenever a plan is attached. When invoices
          exist the KPI strip carries the story; the card gives the plan its
          own line so admin knows which plan is driving the numbers. */}
      {hasPlan && assignment && (
        <Paper
          variant="outlined"
          sx={{
            p: 2, borderRadius: 2,
            borderColor: alpha(theme.palette.primary.main, 0.25),
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Assigned plan
              </Typography>
              <Typography variant="body2" fontWeight={700} noWrap>
                {assignment.planName ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Annual total: {' '}
                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {currencyFmt(assignment.totalAnnualAmount)}
                </strong>
              </Typography>
            </Box>
            <Chip
              size="small"
              label={assignment.status === 'active' ? 'Active' : assignment.status}
              variant="outlined"
              sx={{
                height: 22, fontSize: '0.7rem', fontWeight: 600,
                borderColor: alpha(theme.palette.success.main, 0.32),
                color: theme.palette.success.dark,
                bgcolor: alpha(theme.palette.success.main, 0.08),
              }}
            />
          </Stack>
        </Paper>
      )}

      {!hasBilling && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <ReceiptLong sx={{ fontSize: 28, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
            {hasPlan ? 'No challans generated yet' : 'No billing history yet'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasPlan
              ? 'The plan is attached; run "Generate for period" on the Challans page to bill the current month.'
              : 'Assign a plan to this student’s enrollment on the Fee Plans page to start billing.'}
          </Typography>
        </Paper>
      )}

      {/* Outstanding invoices */}
      {outstandingInvoices.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.warning.main, 0.03) }}>
            <WarningAmber fontSize="small" sx={{ color: theme.palette.warning.dark }} />
            <Typography variant="body2" fontWeight={700}>Outstanding challans</Typography>
            <Chip
              size="small"
              label={outstandingInvoices.length}
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
            />
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2 }}>Challan</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Outstanding</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outstandingInvoices.slice(0, 10).map(inv => (
                <TableRow
                  key={inv._id}
                  hover
                  onClick={() => setDetailInvoiceId(inv._id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ pl: 2 }}>
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <ChallanNumberCell value={inv.invoiceNumber} />
                      {inv.isOverdue && (
                        <Chip
                          size="small"
                          label="Overdue"
                          variant="outlined"
                          sx={{
                            height: 18, fontSize: '0.65rem', fontWeight: 600,
                            borderColor: alpha(theme.palette.error.main, 0.3),
                            color: theme.palette.error.dark,
                            bgcolor: alpha(theme.palette.error.main, 0.06),
                          }}
                        />
                      )}
                      {inv.status === 'partial' && !inv.isOverdue && (
                        <Chip
                          size="small"
                          label="Partial"
                          variant="outlined"
                          sx={{
                            height: 18, fontSize: '0.65rem', fontWeight: 600,
                            borderColor: alpha(theme.palette.warning.main, 0.3),
                            color: theme.palette.warning.dark,
                            bgcolor: alpha(theme.palette.warning.main, 0.06),
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CalendarMonth fontSize="inherit" sx={{ color: 'text.disabled' }} />
                      <Typography variant="body2">{inv.period.label}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{format(new Date(inv.dueDate), 'MMM d, yyyy')}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        color: theme.palette.warning.dark,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {currencyFmt(inv.totals.outstanding)}
                    </Typography>
                    {inv.totals.paid > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        of {currencyFmt(inv.totals.net)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {outstandingInvoices.length > 10 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
              …and {outstandingInvoices.length - 10} more. Open the Invoices page for the full list.
            </Typography>
          )}
        </Paper>
      )}

      {/* Full chronological activity — invoices + payments + reversals in one
          feed, day-grouped. Subsumes the old "Recent payments" section since
          payments appear inline with their surrounding events. */}
      {hasBilling && (
        <StudentFeeActivity studentId={studentId} refreshKey={tick + (refreshKey ?? 0)} />
      )}

      {/* Record payment dialog — shared component */}
      <RecordFeePaymentDialog
        open={payOpen}
        studentId={studentId}
        onClose={() => setPayOpen(false)}
        onRecorded={() => {
          setTick(t => t + 1);
          onMutated?.();
        }}
      />

      {/* Invoice detail dialog */}
      <InvoiceDetailDialog
        open={Boolean(detailInvoiceId)}
        invoiceId={detailInvoiceId}
        onClose={() => setDetailInvoiceId(null)}
        onChanged={() => { setTick(t => t + 1); onMutated?.(); }}
      />
    </Stack>
  );
};

export default StudentFeesTab;
